import { ChartConfig, SectionMarket } from '../SectionMarket';

type DataRow = Record<string, string | number | null | undefined>;

interface StockData {
  intro: string[];
  charts: Array<{
    title: string;
    link?: string;
    description: string;
    data: DataRow[];
  }>;
}

interface StockSectionProps {
  id: string;
  data: StockData;
}

function formatMonth(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
}

function getFidcChartConfig(
  index: number,
  charts: StockData['charts'],
): ChartConfig {
  const chart = charts[index];

  if (!chart) {
    return {
      type: 'column',
      stacked: false,
      monetary: false,
      series: [
        {
          name: 'Sem dados',
          data: [],
        },
      ],
      categories: [],
    };
  }

  const rows = chart.data as DataRow[];
  const hasSaldoDevedor = rows.some((r) => r.saldo_devedor != null);

  if (hasSaldoDevedor) {
    return {
      type: 'column',
      stacked: false,
      monetary: true,
      series: [
        {
          name: 'Saldo Devedor',
          data: rows.map((r) => Number(r.saldo_devedor ?? 0)),
        },
      ],
      categories: rows.map((r) => String(r.ano)),
    };
  }

  if (index === 0) {
    return {
      type: 'line-column',
      series: [
        {
          name: 'PL',
          type: 'column',
          data: rows.map((r) => r['Soma de pl'] as number),
        },
        {
          name: 'Núm de FIDC',
          type: 'line',
          data: rows.map((r) => r['Núm de FIDC'] as number),
        },
      ],
      categories: rows.map((r) => {
        const date = new Date(r.data_ref as string);
        return date.getFullYear().toString();
      }),
    };
  }

  if (index === 1) {
    return {
      type: 'pie',
      series: rows.map((r) => r['Soma de pl'] as number),
      categories: rows.map((r) => r.condominio as string),
    };
  }

  if (index === 2) {
    return {
      type: 'pie',
      series: rows.map((r) => r['Soma de pl'] as number),
      categories: rows.map((r) => r.tipo as string),
    };
  }

  if (index === 3) {
    return {
      type: 'pie',
      series: rows.map((r) => r['Soma de pl'] as number),
      categories: rows.map((r) => r.ativo_lastro as string),
    };
  }

  return {
    type: 'line-column',
    series: [
      {
        name: 'Patrimônio Líquido',
        type: 'column',
        data: rows.map((r) => r['Patrimônio Líquido'] as number),
      },
      {
        name: 'Núm de FIDC MM',
        type: 'line',
        data: rows.map((r) => r['Núm de FIDC MM'] as number),
      },
    ],
    categories: rows.map((r) => {
      const date = new Date(r.data_ref as string);
      return date.getFullYear().toString();
    }),
  };
}

function getFiagroChartConfig(
  index: number,
  charts: StockData['charts'],
): ChartConfig {
  const chart = charts[index];

  if (!chart) {
    return {
      type: 'column',
      stacked: false,
      monetary: false,
      series: [
        {
          name: 'Sem dados',
          data: [],
        },
      ],
      categories: [],
    };
  }

  const rows = chart.data as DataRow[];

  // Chart 0: Número de FIAGROs ao longo do ano
  if (index === 0) {
    return {
      type: 'column',
      stacked: false,
      monetary: false,
      series: [
        {
          name: 'Número de Fundos',
          data: rows.map((r) => Number(r.numero_fundos ?? 0)),
        },
      ],
      categories: rows.map((r) => formatMonth(r.mes_referencia as string)),
    };
  }

  // Chart 1: PL total ao longo do ano
  if (index === 1) {
    return {
      type: 'column',
      stacked: false,
      monetary: true,
      series: [
        {
          name: 'PL Total',
          data: rows.map((r) => Number(r.pl_total ?? 0)),
        },
      ],
      categories: rows.map((r) => formatMonth(r.data_referencia as string)),
    };
  }

  // Chart 2: PL por Anexo Normativo (pie)
  if (index === 2) {
    return {
      type: 'pie',
      series: rows.map((r) => Number(r.pl_total ?? 0)),
      categories: rows.map((r) => String(r.anexo)),
    };
  }

  // Chart 3: PL por categoria de carteira investida (pie)
  if (index === 3) {
    return {
      type: 'pie',
      series: rows.map((r) => Number(r.pl_total ?? 0)),
      categories: rows.map((r) => String(r.categoria_carteira_investida)),
    };
  }

  // Chart 4: PL fundos com cogestão (pie)
  if (index === 4) {
    return {
      type: 'pie',
      series: rows.map((r) => Number(r.pl_total ?? 0)),
      categories: rows.map((r) => String(r.tipo_cogestao)),
    };
  }

  // Chart 5: Distribuição por categoria (pie — número de fundos)
  if (index === 5) {
    return {
      type: 'pie',
      series: rows.map((r) => Number(r.numero_fundos ?? 0)),
      categories: rows.map((r) => String(r.categoria_carteira_investida)),
    };
  }

  // Chart 6: Carteira vencida e a vencer (line-column)
  return {
    type: 'line-column',
    series: [
      {
        name: 'A Vencer',
        type: 'column',
        data: rows.map((r) => Number(r.a_vencer_total ?? 0)),
      },
      {
        name: 'Vencidos',
        type: 'column',
        data: rows.map((r) => Number(r.vencidos_total ?? 0)),
      },
    ],
    categories: rows.map((r) => formatMonth(r.data as string)),
  };
}

function getFiiChartConfig(
  index: number,
  charts: StockData['charts'],
): ChartConfig {
  const chart = charts[index];

  if (!chart) {
    return {
      type: 'column',
      stacked: false,
      monetary: false,
      series: [
        {
          name: 'Sem dados',
          data: [],
        },
      ],
      categories: [],
    };
  }

  const rows = chart.data as DataRow[];

  if (index === 0) {
    return {
      type: 'line-column',
      series: [
        {
          name: 'Patrimonio Liquido',
          type: 'column',
          data: rows.map((r) => Number(r.pl_total ?? 0)),
        },
        {
          name: 'Numero de Fundos',
          type: 'line',
          data: rows.map((r) => Number(r.numero_fundos ?? 0)),
        },
      ],
      categories: rows.map((r) => String(r.ano)),
    };
  }

  if (index === 1) {
    return {
      type: 'pie',
      series: rows.map((r) => Number(r.pl_total ?? 0)),
      categories: rows.map((r) => String(r.tipo_ativo ?? 'Nao informado')),
    };
  }

  if (index === 2) {
    return {
      type: 'pie',
      series: rows.map((r) => Number(r.pl_total ?? 0)),
      categories: rows.map((r) => String(r.finalidade ?? 'Nao informado')),
    };
  }

  return {
    type: 'pie',
    series: rows.map((r) => Number(r.pl_total ?? 0)),
    categories: rows.map((r) => String(r.tipo_gestao ?? 'Nao informado')),
  };
}

export function StockSection({ id, data }: StockSectionProps) {
  const isfiagro = data.charts.some((c) =>
    c.data.some((r) => r.numero_fundos != null && r.mes_referencia != null),
  );
  const isFii = data.charts.some((c) =>
    c.data.some((r) => r.pl_total != null && r.ano != null),
  );

  return (
    <SectionMarket
      id={id}
      title="Estoque"
      intro={data.intro}
      charts={data.charts}
      getChartConfig={(index) =>
        isfiagro
          ? getFiagroChartConfig(index, data.charts)
          : isFii
            ? getFiiChartConfig(index, data.charts)
            : getFidcChartConfig(index, data.charts)
      }
      gradientPosition="left"
    />
  );
}
